import React, { useId } from "react";
import { Box, Typography } from "@mui/material";
import { Language } from "@/api/data-contracts";
import { VisitorFlagCountry, visitorLanguageFlag } from "@/app/locale/visitorLanguage";

const FLAG_WIDTH = 21;
const FLAG_HEIGHT = 14;

const svgProps = {
    viewBox: "0 0 60 40",
    width: "100%",
    height: "100%",
    preserveAspectRatio: "none",
    "aria-hidden": true,
    focusable: "false"
} as const;

const GermanFlag = () => (
    <svg {...svgProps}>
        <rect width="60" height="40" fill="#FFCE00" />
        <rect width="60" height="26.67" fill="#DD0000" />
        <rect width="60" height="13.33" fill="#000000" />
    </svg>
);

const PolishFlag = () => (
    <svg {...svgProps}>
        <rect width="60" height="40" fill="#DC143C" />
        <rect width="60" height="20" fill="#FFFFFF" />
    </svg>
);

const BritishFlag = () => {
    // The flag is rendered in the switch and in its open list at the same time, so the clip path id must be unique
    // per instance; useId may contain characters that break a url(#...) reference.
    const clipId = `union-jack-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
    const diagonals = "M0,0 L60,40 M60,0 L0,40";
    const cross = "M30,0 v40 M0,20 h60";
    return (
        <svg {...svgProps}>
            <clipPath id={clipId}>
                <path d="M30,20 h30 v20 z v20 h-30 z h-30 v-20 z v-20 h30 z" />
            </clipPath>
            <rect width="60" height="40" fill="#012169" />
            <path d={diagonals} stroke="#FFFFFF" strokeWidth="8" />
            <path d={diagonals} clipPath={`url(#${clipId})`} stroke="#C8102E" strokeWidth="5" />
            <path d={cross} stroke="#FFFFFF" strokeWidth="13" />
            <path d={cross} stroke="#C8102E" strokeWidth="8" />
        </svg>
    );
};

const verticalTricolour = (left: string, middle: string, right: string) => {
    const Tricolour = () => (
        <svg {...svgProps}>
            <rect width="20" height="40" fill={left} />
            <rect x="20" width="20" height="40" fill={middle} />
            <rect x="40" width="20" height="40" fill={right} />
        </svg>
    );
    return Tricolour;
};

const ItalianFlag = verticalTricolour("#009246", "#FFFFFF", "#CE2B37");

const FrenchFlag = verticalTricolour("#002654", "#FFFFFF", "#ED2939");

const CzechFlag = () => (
    <svg {...svgProps}>
        <rect width="60" height="40" fill="#D7141A" />
        <rect width="60" height="20" fill="#FFFFFF" />
        <path d="M0,0 L30,20 L0,40 z" fill="#11457E" />
    </svg>
);

const FLAGS: Record<VisitorFlagCountry, React.FC> = {
    DE: GermanFlag,
    GB: BritishFlag,
    PL: PolishFlag,
    IT: ItalianFlag,
    FR: FrenchFlag,
    CZ: CzechFlag
};

const CountryFlag = ({ country }: { country: VisitorFlagCountry }) => {
    const Flag = FLAGS[country];
    return <Flag />;
};

/**
 * The flag of a language in a fixed 3:2 box, or its language code where no flag is assigned. Purely decorative: the
 * surrounding control carries the accessible name. The border keeps the white edge of the Polish and the Czech
 * flag visible on a light bar.
 */
export const LanguageFlag = ({ language }: { language: Language }) => {
    const flag = visitorLanguageFlag(language);
    const content =
        flag.kind === "flag" ? (
            <CountryFlag country={flag.country} />
        ) : (
            <Typography component="span" sx={{ fontSize: 9, fontWeight: "bold", lineHeight: 1 }}>
                {flag.code}
            </Typography>
        );
    return (
        <Box
            component="span"
            aria-hidden
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                width: FLAG_WIDTH,
                height: FLAG_HEIGHT,
                boxSizing: "content-box",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                overflow: "hidden"
            }}
        >
            {content}
        </Box>
    );
};
